import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { SaleError, balanceLabel, formatQty, normalizeCode, toMilli, uuidv7, variantLabel, type PaymentLine } from '@pos/core';
import { recordSaleLocally, type SaleRow } from '@pos/local-db';
import { timed } from '../../lib/timings.ts';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Card, Kbd, Spinner, cx, errorMessage } from '../../ui/index.tsx';
import { Dialog, closeDialog } from '../../ui/Dialog.tsx';
import { BarcodeIcon, CameraIcon, CheckIcon } from '../../ui/icons.tsx';
import { ProductPicker, type PickedVariant } from '../products/ProductPicker.tsx';
import { useCatalog, useMoney } from '../products/data.ts';
import { useStockMap } from '../stock/data.ts';
import { CameraScanner, cameraScanSupported } from './CameraScanner.tsx';
import { CheckoutDialog } from './CheckoutDialog.tsx';
import { OpenCashDialog } from '../cash/CashDialogs.tsx';
import { useOpenSession } from '../cash/data.ts';
import { CustomerPickerDialog } from '../customers/CustomerDialogs.tsx';
import { useBalances } from '../customers/data.ts';
import { DiscountDialog, LineDialog, QtyDialog } from './PosDialogs.tsx';
import { EMPTY_CART, addToCart, buildSale, cartTotals, qtyInCart, removeLine, setLineQty, type Product } from './cart.ts';
import { useCart, useMaxDiscount, usePosSettings } from './state.ts';
import { useTicket } from './useTicket.ts';
import { useCanWrite } from '../../services/license.ts';
import { ReadOnlyNotice } from '../license/LicenseNotices.tsx';
import { PriceCheck } from './PriceCheck.tsx';

type Notice = { tone: 'warning' | 'error'; text: string } | null;

/** Punto de venta: buscar o escanear, carrito, descuentos y cobro. Funciona sin conexión. */
export function PosPage() {
  const { business, device, membership, writer, can, isOwner } = useSession();
  const catalog = useCatalog();
  const stock = useStockMap();
  const money = useMoney();
  const settings = usePosSettings();
  const maxDiscount = useMaxDiscount();
  const ticket = useTicket();
  const [cart, setCart] = useCart();
  const [weighing, setWeighing] = useState<Product | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [camera, setCamera] = useState(false);
  const [done, setDone] = useState<SaleRow | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const search = useRef<HTMLInputElement>(null);
  const page = useRef<HTMLDivElement>(null);
  const lines = useRef<HTMLUListElement>(null);
  const lineButtons = () => [...(lines.current?.querySelectorAll<HTMLButtonElement>('button[data-line]') ?? [])];
  const cashSession = useOpenSession();
  const balances = useBalances();
  const customer = useLiveQuery(async () => (cart.customerId ? ((await db.customers.get(cart.customerId)) ?? null) : null), [cart.customerId]);
  // Cliente de la venta recién registrada: en «Venta registrada», lo que quedó a su cuenta y su saldo.
  const doneCustomer = useLiveQuery(async () => (done?.customer_id ? ((await db.customers.get(done.customer_id)) ?? null) : null), [done?.customer_id]);
  const [pickingCustomer, setPickingCustomer] = useState(false);
  // Cliente elegido desde «Cobrar» (al tocar «Cuenta corriente» sin cliente): lo que corre al elegirlo.
  const afterPick = useRef<(() => void) | null>(null);
  const [openingCash, setOpeningCash] = useState(false);
  // Caja obligatoria: sin la caja abierta (la compartida o una de este dispositivo) no se cobra.
  const canWrite = useCanWrite();
  const cashMissing = canWrite && settings.require_open_cash && cashSession === null;

  const totals = useMemo(() => cartTotals(cart), [cart]);
  const focusSearch = () => setTimeout(() => search.current?.focus(), 50);

  const addQty = useCallback(
    (product: Product, qtyMilli: number) => {
      const s = stock?.get(product.variantId);
      const after = qtyInCart(cart, product.variantId) + qtyMilli;
      if (s?.tracked && after > s.qty) {
        const left = `Quedan ${formatQty(Math.max(0, s.qty), product.unitType)} de ${product.label}.`;
        if (settings.negative_stock === 'block') {
          setNotice({ tone: 'error', text: `No hay stock suficiente. ${left}` });
          return;
        }
        setNotice({ tone: 'warning', text: `Ojo: no alcanza el stock. ${left} Se vende igual.` });
      } else {
        setNotice(null);
      }
      setCart((c) => addToCart(c, product, qtyMilli));
      setFlash(product.variantId);
    },
    [cart, stock, settings.negative_stock, setCart],
  );

  const pick = useCallback(
    (picked: PickedVariant, qty: number | null) => {
      const product: Product = {
        variantId: picked.variant.id,
        label: picked.label,
        unitType: picked.item.product.unit_type,
        price: picked.variant.price,
        taxBps: picked.item.product.tax_bps,
      };
      if (product.unitType !== 'unit' && qty === null) {
        setWeighing(product);
        return;
      }
      const qtyMilli = toMilli(qty ?? 1);
      if (product.unitType === 'unit' && qtyMilli % 1000 !== 0) {
        setNotice({ tone: 'error', text: `${product.label} se vende por unidad: la cantidad tiene que ser entera.` });
        return;
      }
      addQty(product, qtyMilli);
    },
    [addQty],
  );

  const onCameraCode = useCallback(
    (raw: string) => {
      if (!catalog) return;
      const code = normalizeCode(raw);
      for (const item of catalog.items) {
        if (!item.product.active) continue;
        const variant = item.variants.find((v) => v.active && v.barcode === code);
        if (variant) {
          const attrs = variantLabel(variant.attributes);
          pick({ item, variant, label: attrs ? `${item.product.name} · ${attrs}` : item.product.name }, null);
          return;
        }
      }
      setNotice({ tone: 'error', text: `No hay ningún producto con el código ${code}.` });
    },
    [catalog, pick],
  );

  // Atajos de teclado (PC de mostrador): F2 cobrar, F4 descuento.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (document.querySelector('dialog[open]')) return;
      if (e.key === 'F8') {
        e.preventDefault();
        setPickingCustomer(true);
      } else if (e.key === 'F2' && cart.lines.length > 0 && !cashMissing && canWrite) {
        e.preventDefault();
        setError(null);
        setCheckout(true);
      } else if (e.key === 'F4' && cart.lines.length > 0 && maxDiscount > 0) {
        e.preventDefault();
        setDiscountOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cart.lines.length, maxDiscount, cashMissing, canWrite]);

  // PC de mostrador: después de un clic en la pantalla de venta (un espacio vacío, − / +, Vaciar…)
  // el cursor vuelve solo al buscador, para escanear o escribir el próximo producto sin volver a
  // tocarlo. No si quedó un aviso abierto o el clic fue en otro campo de texto, ni con el teclado
  // (Enter sobre «+» sigue ahí para repetir), ni en pantallas táctiles (abriría el teclado).
  useEffect(() => {
    if (matchMedia('(pointer: coarse)').matches) return;
    // En la captura, antes que la app: el botón tocado (p. ej. «Vaciar») todavía está en la página.
    const onClick = (e: MouseEvent) => {
      if (e.detail === 0 || !page.current?.contains(e.target as Node)) return;
      setTimeout(() => {
        const active = document.activeElement;
        if (document.querySelector('dialog[open]') || active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
        search.current?.focus();
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 600);
    return () => clearTimeout(t);
  }, [flash]);

  const confirm = async (payments: PaymentLine[]) => {
    setBusy(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const sale = buildSale(cart, payments, maxDiscount, {
        businessId: business.id,
        deviceId: device.id,
        sellerId: membership.user_id,
        cashSessionId: cashSession?.id ?? null,
        now,
        newId: uuidv7,
      });
      const saved = await timed('Cobro: guardar la venta en el dispositivo', () => recordSaleLocally(db, sale, device.code, writer));
      setCart(EMPTY_CART);
      setCheckout(false);
      setNotice(null);
      setDone(saved);
      requestSync(0);
    } catch (e) {
      setError(e instanceof SaleError ? e.message : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!catalog || !stock) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  // Modo consulta: el dueño solo puede ver precios; los empleados, nada.
  if (!canWrite) return isOwner ? <PriceCheck catalog={catalog} stock={stock} money={money} /> : <ReadOnlyNotice />;

  if (catalog.items.length === 0) {
    return (
      <Card className="mx-auto max-w-lg text-center">
        <p className="font-medium">Todavía no hay productos cargados.</p>
        <p className="mt-1 text-sm text-slate-500">Cargalos (o importalos desde Excel) y vas a poder vender.</p>
        <Link to="/productos" className="mt-3 inline-block text-sm font-medium text-brand">
          Ir a productos
        </Link>
      </Card>
    );
  }

  const count = cart.lines.length;
  const canCharge = count > 0 && !cashMissing && canWrite;
  const charge = () => {
    if (!canCharge) return;
    setError(null);
    setCheckout(true);
  };
  const customerBalance = customer ? (balances?.get(customer.id) ?? 0) : 0;

  const totalsPanel = (
    <div className="flex flex-1 flex-col gap-4">
      <div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Total</span>
          {count > 0 && (
            <span className="text-xs text-slate-500">
              {count} {count === 1 ? 'producto' : 'productos'}
            </span>
          )}
        </div>
        <p className="mt-1 text-4xl font-semibold tracking-tight text-slate-900 tabular-nums">{money(totals.baseTotal)}</p>
        {totals.discountTotal > 0 && (
          <div className="mt-3 space-y-1 border-t border-slate-100 pt-3">
            <Row label="Subtotal" value={money(totals.subtotal)} />
            <Row label="Descuentos" value={`−${money(totals.discountTotal)}`} />
          </div>
        )}
      </div>
      <div className="flex gap-2">
        {maxDiscount > 0 && (
          <Button variant="secondary" size="lg" disabled={!canCharge} onClick={() => setDiscountOpen(true)}>
            Descuento
          </Button>
        )}
        <Button size="lg" block disabled={!canCharge} onClick={charge}>
          Cobrar
        </Button>
      </div>
      <p className="text-xs text-slate-500">Los recargos o descuentos por medio de pago se aplican al cobrar.</p>
      <div className="mt-auto hidden space-y-2 border-t border-slate-100 pt-3 text-xs text-slate-500 lg:block">
        <p className="font-semibold tracking-wide uppercase">Atajos</p>
        <ul className="space-y-1.5">
          <li className="flex items-center justify-between gap-2">
            Cobrar (con el buscador vacío)
            <span className="flex items-center gap-1">
              <Kbd>Enter</Kbd> o <Kbd>F2</Kbd>
            </span>
          </li>
          <li className="flex items-center justify-between gap-2">
            Descuento <Kbd>F4</Kbd>
          </li>
          <li className="flex items-center justify-between gap-2">
            Cliente <Kbd>F8</Kbd>
          </li>
          <li className="flex items-center justify-between gap-2">
            Corregir los productos
            <span className="flex items-center gap-1">
              <Kbd>↑</Kbd> <Kbd>↓</Kbd>
            </span>
          </li>
        </ul>
        <p>
          En un producto: <Kbd>+</Kbd> <Kbd>−</Kbd> cambian la cantidad, <Kbd>Supr</Kbd> lo quita, <Kbd>Enter</Kbd> lo edita y <Kbd>Esc</Kbd> vuelve al buscador.
        </p>
        <p>
          Varias unidades: la cantidad y un asterisco antes del producto. Ej.: <span className="font-mono whitespace-nowrap">3*coca</span> agrega 3.
        </p>
      </div>
    </div>
  );

  return (
    <div ref={page} className="mx-auto max-w-7xl pb-28 lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start lg:gap-5 lg:pb-0 xl:grid-cols-[minmax(0,1fr)_380px]">
      {/* La venta ocupa todo el alto de la pantalla (sin espacio vacío abajo). Se resta lo que hay
          arriba y abajo: barra superior (celular), márgenes y el total fijo de abajo (celular y tablet). */}
      <section className="flex min-h-[calc(100dvh_-_17.5rem_-_1px_-_env(safe-area-inset-top))] flex-col gap-3 md:min-h-[calc(100dvh-9.5rem)] lg:min-h-[calc(100dvh-2.75rem)]">
        <div className="flex gap-2">
          <div className="min-w-0 flex-1">
            <ProductPicker
              catalog={catalog}
              onPick={pick}
              qtyPrefix
              autoFocus
              inputRef={search}
              onEmptyEnter={charge}
              onEmptyArrow={(dir) => {
                const buttons = lineButtons();
                (dir < 0 ? buttons.at(-1) : buttons[0])?.focus();
              }}
              placeholder="Escaneá o buscá un producto…"
              renderMeta={(o) => {
                const s = stock.get(o.variant.id);
                return (
                  <>
                    <span className="block font-medium text-slate-800">{money(o.variant.price)}</span>
                    {s?.tracked && <span className={cx(s.qty <= 0 && 'text-red-700')}>stock {formatQty(s.qty, o.item.product.unit_type)}</span>}
                  </>
                );
              }}
            />
          </div>
          {cameraScanSupported && (
            <Button variant="secondary" size="lg" onClick={() => setCamera(true)} aria-label="Escanear con la cámara">
              <CameraIcon className="size-5" />
            </Button>
          )}
        </div>
        {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
        {cashMissing && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
            <span>La caja está cerrada: abrila para poder cobrar.</span>
            <Button size="sm" onClick={() => setOpeningCash(true)}>
              Abrir caja
            </Button>
          </div>
        )}

        <Card flush className="flex flex-1 flex-col">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 sm:px-4">
            <h2 className="font-semibold tracking-tight">Venta actual {count > 0 && <span className="font-normal text-slate-500">· {count} {count === 1 ? 'producto' : 'productos'}</span>}</h2>
            <div className="flex items-center gap-1">
              {customer ? (
                <span className="flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pr-1 pl-3 text-sm">
                  <button type="button" className="max-w-40 truncate font-medium" onClick={() => setPickingCustomer(true)}>
                    {customer.name}
                    {can('customer_accounts') && customerBalance !== 0 && <span className="ml-1 font-normal text-slate-500">({balanceLabel(customerBalance, money)})</span>}
                  </button>
                  <button
                    type="button"
                    className="rounded-full px-1.5 text-slate-500 hover:bg-slate-200"
                    aria-label="Quitar cliente"
                    onClick={() => {
                      setCart((c) => ({ ...c, customerId: null }));
                      focusSearch();
                    }}
                  >
                    ✕
                  </button>
                </span>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setPickingCustomer(true)}>
                  + Cliente
                </Button>
              )}
              {count > 0 && (
                <Button
                  variant="dangerGhost"
                  size="sm"
                  onClick={() => {
                    setCart((c) => ({ ...EMPTY_CART, customerId: c.customerId }));
                    focusSearch();
                  }}
                >
                  Vaciar
                </Button>
              )}
            </div>
          </div>
          {count === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
              <span className="flex size-14 items-center justify-center rounded-2xl bg-brand/10 text-brand ring-1 ring-brand/20">
                <BarcodeIcon className="size-7" />
              </span>
              <div>
                <p className="font-medium text-slate-800">Escaneá o buscá un producto para empezar</p>
                <p className="mt-1 text-sm text-slate-500">Con el lector, el nombre o el código: se agrega a la venta.</p>
              </div>
            </div>
          ) : (
            <ul
              ref={lines}
              className="divide-y divide-slate-100"
              // Teclado en los productos (se entra con ↑ ↓ desde el buscador vacío): las flechas mueven
              // (pasando el primero o el último se vuelve al buscador), + / − cambian la cantidad (− no
              // baja de 1), Supr quita el producto, Enter lo abre para editarlo y Escape vuelve al buscador.
              onKeyDown={(e) => {
                const buttons = lineButtons();
                const i = buttons.indexOf(e.target as HTMLButtonElement);
                const line = cart.lines[i];
                if (!line) return;
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  (buttons[i + (e.key === 'ArrowDown' ? 1 : -1)] ?? search.current)?.focus();
                } else if (e.key === '+' || e.key === '-') {
                  e.preventDefault();
                  // Por peso, litro o metro la cantidad se escribe.
                  if (line.unitType !== 'unit') setEditing(i);
                  else if (e.key === '+') addQty({ variantId: line.variantId, label: line.label, unitType: line.unitType, price: line.unitPrice, taxBps: line.taxBps }, 1000);
                  else if (line.qtyMilli > 1000) setCart((c) => setLineQty(c, i, line.qtyMilli - 1000));
                } else if (e.key === 'Delete') {
                  e.preventDefault();
                  // El foco pasa al producto que queda en ese lugar (o al anterior); si no queda ninguno, al buscador.
                  (buttons[i + 1] ?? buttons[i - 1] ?? search.current)?.focus();
                  setCart((c) => removeLine(c, i));
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  search.current?.focus();
                }
              }}
            >
              {cart.lines.map((line, i) => {
                const t = totals.lines[i]!;
                const s = stock.get(line.variantId);
                const short = s?.tracked && qtyInCart(cart, line.variantId) > s.qty;
                return (
                  <li
                    key={`${line.variantId}-${line.unitPrice}`}
                    className={cx(
                      'flex items-center gap-2 px-3 py-2.5 transition-colors hover:bg-slate-50 sm:px-4',
                      'has-[[data-line]:focus-visible]:bg-brand/10 has-[[data-line]:focus-visible]:ring-2 has-[[data-line]:focus-visible]:ring-brand/60 has-[[data-line]:focus-visible]:ring-inset',
                      flash === line.variantId && 'bg-emerald-50',
                    )}
                  >
                    <button type="button" data-line className="min-w-0 flex-1 text-left focus-visible:outline-none" onClick={() => setEditing(i)}>
                      <p className="truncate font-medium">{line.label}</p>
                      <p className="truncate text-sm text-slate-500">
                        {formatQty(line.qtyMilli, line.unitType)} × {money(line.unitPrice)}
                        {t.discount > 0 && <span className="ml-2 text-emerald-700">−{money(t.discount)}</span>}
                        {short && <span className="ml-2 text-amber-700">sin stock suficiente</span>}
                      </p>
                    </button>
                    {line.unitType === 'unit' && (
                      <div className="flex shrink-0 items-center gap-1">
                        <Button variant="secondary" size="sm" aria-label={`Restar ${line.label}`} onClick={() => setCart((c) => setLineQty(c, i, line.qtyMilli - 1000))}>
                          −
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          aria-label={`Sumar ${line.label}`}
                          onClick={() => addQty({ variantId: line.variantId, label: line.label, unitType: line.unitType, price: line.unitPrice, taxBps: line.taxBps }, 1000)}
                        >
                          +
                        </Button>
                      </div>
                    )}
                    <span className="w-24 shrink-0 text-right font-semibold tabular-nums">{money(t.total)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </section>

      {/* PC y tablet: panel de totales al costado. */}
      <aside className="hidden lg:sticky lg:top-5 lg:block">
        <Card className="flex flex-col lg:min-h-[calc(100dvh-2.75rem)]">{totalsPanel}</Card>
      </aside>

      {/* Celular: total y "Cobrar" fijos abajo, sobre la navegación. */}
      <div className="pb-safe fixed inset-x-0 bottom-16 z-10 border-t border-slate-200 bg-white px-4 py-3 shadow-[0_-6px_16px_-8px_rgb(15_23_42/0.18)] md:bottom-0 md:left-60 lg:hidden">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-slate-500">{totals.discountTotal > 0 ? `Con descuento de ${money(totals.discountTotal)}` : 'Total'}</p>
            <p className="text-2xl font-semibold tabular-nums">{money(totals.baseTotal)}</p>
          </div>
          {maxDiscount > 0 && (
            <Button variant="secondary" disabled={!canCharge} onClick={() => setDiscountOpen(true)}>
              Desc.
            </Button>
          )}
          <Button size="lg" disabled={!canCharge} onClick={charge}>
            Cobrar
          </Button>
        </div>
      </div>

      <QtyDialog
        product={weighing}
        money={money}
        onClose={() => {
          setWeighing(null);
          focusSearch();
        }}
        onConfirm={(qty) => {
          if (weighing) addQty(weighing, qty);
          setWeighing(null);
          focusSearch();
        }}
      />
      <LineDialog
        cart={cart}
        index={editing}
        maxDiscount={maxDiscount}
        money={money}
        onChange={setCart}
        onClose={() => {
          setEditing(null);
          focusSearch();
        }}
      />
      <DiscountDialog
        open={discountOpen}
        cart={cart}
        maxDiscount={maxDiscount}
        money={money}
        onChange={setCart}
        onClose={() => {
          setDiscountOpen(false);
          focusSearch();
        }}
      />
      <CheckoutDialog
        open={checkout}
        baseTotal={totals.baseTotal}
        settings={settings}
        customer={
          customer
            ? {
                name: customer.name,
                balance: customerBalance,
                creditLimit: customer.credit_limit,
                canExceedLimit: isOwner,
              }
            : null
        }
        canUseAccount={can('customer_accounts')}
        money={money}
        busy={busy}
        error={error}
        onConfirm={confirm}
        onClose={() => {
          setCheckout(false);
          focusSearch();
        }}
        onPickCustomer={(onPicked) => {
          afterPick.current = onPicked;
          setPickingCustomer(true);
        }}
      />
      <SaleDoneDialog
        sale={done}
        account={doneCustomer ? { name: doneCustomer.name, balance: balances ? (balances.get(doneCustomer.id) ?? 0) : null } : null}
        money={money}
        search={search}
        onPrint={() => done && ticket.print(done, null, { withBalance: true })}
        onWhatsapp={() => done && ticket.whatsapp(done, null, { withBalance: true })}
        onClose={() => {
          setDone(null);
          focusSearch();
        }}
      />
      {cameraScanSupported && <CameraScanner open={camera} onClose={() => setCamera(false)} onCode={onCameraCode} />}
      {/* Abierto desde «Cobrar», queda encima y al cerrarse el foco vuelve ahí (no al buscador). */}
      <CustomerPickerDialog
        open={pickingCustomer}
        onClose={() => {
          setPickingCustomer(false);
          if (afterPick.current) afterPick.current = null;
          else focusSearch();
        }}
        onPick={(c) => {
          setCart((cur) => ({ ...cur, customerId: c.id }));
          setPickingCustomer(false);
          const then = afterPick.current;
          afterPick.current = null;
          if (then) then();
          else focusSearch();
        }}
      />
      <OpenCashDialog
        open={openingCash}
        onClose={() => {
          setOpeningCash(false);
          focusSearch();
        }}
        onOpened={focusSearch}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm text-slate-600">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

/** Flechas entre las opciones de «Venta registrada» (WhatsApp, Imprimir ticket, Nueva venta). */
const CHOICE_STEPS: Record<string, number> = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 };

function SaleDoneDialog({
  sale,
  account,
  money,
  search,
  onPrint,
  onWhatsapp,
  onClose,
}: {
  sale: SaleRow | null;
  /** Cliente de la venta (null si no tiene): si quedó algo a su cuenta corriente, se muestra con su saldo. */
  account: { name: string; balance: number | null } | null;
  money: (c: number) => string;
  /** Buscador de productos: lo que se escribe con el aviso abierto va ahí. */
  search: RefObject<HTMLInputElement | null>;
  onPrint: () => void;
  onWhatsapp: () => void;
  onClose: () => void;
}) {
  const change = sale ? sale.payments.reduce((sum, p) => sum + (p.method === 'cash' && p.tendered != null ? p.tendered - p.amount : 0), 0) : 0;
  const onAccount = sale ? sale.payments.reduce((sum, p) => sum + (p.method === 'account' ? p.amount : 0), 0) : 0;
  return (
    <Dialog
      open={!!sale}
      onClose={onClose}
      title="Venta registrada"
      onKeyDown={(e) => {
        // Escribir o escanear el próximo producto: se cierra el aviso y lo tipeado va al buscador.
        // Se cierra acá mismo (no al render siguiente) para que el buscador reciba el foco antes de
        // que el navegador escriba la letra: así no se pierde ninguna.
        if (e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
          closeDialog(e.currentTarget);
          search.current?.focus();
          onClose();
          return;
        }
        const step = CHOICE_STEPS[e.key];
        if (!step) return;
        const buttons = [...e.currentTarget.querySelectorAll('button')];
        const current = buttons.indexOf(e.target as HTMLButtonElement);
        e.preventDefault();
        // Si el foco no está en una opción (p. ej. se tocó el texto), va a «Nueva venta».
        (current < 0 ? buttons.at(-1) : buttons[current + step])?.focus();
      }}
      footer={
        <>
          <Button variant="secondary" onClick={onWhatsapp}>
            WhatsApp
          </Button>
          <Button variant="secondary" onClick={onPrint}>
            Imprimir ticket
          </Button>
          <Button autoFocus onClick={onClose}>
            Nueva venta
          </Button>
        </>
      }
    >
      {sale && (
        <div className="space-y-3 text-center">
          <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
            <CheckIcon className="size-6" />
          </span>
          <p className="text-sm text-slate-500">Venta {sale.number}</p>
          <p className="text-3xl font-semibold tracking-tight tabular-nums">{money(sale.total)}</p>
          {change > 0 && (
            <div className="rounded-xl bg-emerald-50 p-3 ring-1 ring-emerald-200">
              <p className="text-sm text-emerald-900">Vuelto</p>
              <p className="text-3xl font-bold text-emerald-900 tabular-nums">{money(change)}</p>
            </div>
          )}
          {onAccount > 0 && account && (
            <div className="rounded-xl bg-amber-50 p-3 ring-1 ring-amber-200">
              <p className="text-sm text-amber-900">Quedó a cuenta de {account.name}</p>
              <p className="text-3xl font-bold text-amber-900 tabular-nums">{money(onAccount)}</p>
              {account.balance != null && <p className="mt-1 text-sm font-medium text-amber-900">Saldo: {balanceLabel(account.balance, money)}</p>}
            </div>
          )}
          <p className="text-xs text-slate-500">{navigator.onLine ? 'Se envía al servidor en segundo plano.' : 'Guardada en este dispositivo: se envía cuando vuelva la conexión.'}</p>
          <p className="hidden text-xs text-slate-500 lg:block">
            <span className="font-medium">Teclado:</span> flechas para elegir y Enter · para seguir vendiendo, escribí o escaneá el próximo producto.
          </p>
        </div>
      )}
    </Dialog>
  );
}
